<template>
  <div class="cookie-notice-wrapper">
    <div class="cookie-modal-main cookie-modal-main-notice">
      <div class="cookie-modal-main-content">
        <div class="cookie-modal-container">
          <div class="cookie-top-content">
            <p class="cookie-top-content-body">
              <span v-html="t(
                'cookies.notice.main',
                { params: [{ shopname: eshopName, policyCookies: baseCookiesUrl }] }
                )"
              ></span>
              <span class="cookie-top-btn-container">
                <button
                  class="cookie-top-content-accept-essentials-btn"
                  data-action="consentToCookiesEssential"
                  @click="postCookieSettingsEssentials"
                >
                  <span data-action="consentToCookiesEssential">{{ t('cookies.accept_essentials') }}</span>
                </button>
                <button
                  v-if="cookieDetailsEnabled"
                  class="cookie-top-content-details"
                  @click="toggleDetailsTable"
                >
                  <span class="cookie-top-content-details-text">
                    {{ t('cookies.details.btn') }}
                  </span>
                  <em
                    :class="['ti-close', { 'ti-angle-down': !showCookieDetails, 'ti-angle-up': showCookieDetails }]"></em>
                </button>
              </span>
            </p>
          </div>
          <div class="cookie-bottom-content">
            <button
              class="cookie-bottom-content-accept-btn"
              data-action="consentToCookiesAll"
              @click="postCookieSettingsAllGrantedAction"
            >
              <span data-action="consentToCookiesAll">{{ t('cookies.accept') }}</span>
            </button>
            <button class="cookie-bottom-content-settings-btn" @click="openSettings">{{ t('cookies.more') }}</button>
          </div>
        </div>

        <transition name="fade">
          <div v-show="showCookieDetails && cookieDetailsEnabled" class="cookie-details">
            <div class="cookie-details-sidebar">
              <div class="cookie-details-sidebar-header">
                <span>{{ t('cookies.details.title') }}</span>
              </div>
              <div v-if="detailsTableDataLoading" class="cookie-details-skeleton">
                <div v-for="i in 3" :key="i" class="skeleton-row"></div>
              </div>
              <transition name="fade">
                <div v-show="!detailsTableDataLoading" class="native_slider cookie-details-sidebar-content">
                  <ol class="cookie-details-sidebar-list native_slider-lg">
                    <li v-for="category in cookieCategories"
                        :key="category"
                        :class="{ 'active': category === selectedCategory }"
                        class="cookie-details-sidebar-list-item native_slider-li"
                        @click="selectCategory(category)"
                    >
                      <span class="cookie-details-sidebar-list-item-text">{{ category }}</span>
                    </li>
                  </ol>
                </div>
              </transition>
            </div>
            <div v-if="detailsTableDataLoading" class="cookie-details-skeleton">
              <div v-for="i in 5" :key="i" class="skeleton-row"></div>
            </div>
            <transition name="fade">
              <SimpleTable
                v-show="!detailsTableDataLoading"
                :columns="columns"
                :rows="filteredRows"
                :sort-icon-ascending="'ti-close ti-angle-up'"
                :sort-icon-descending="'ti-close ti-angle-down'"
                class="cookie-details-table"
              >
                <template v-slot:[`platforms-data`]="{ row }">
                  <a v-if="row.links" :href="row.links" target="_blank" rel="noopener noreferrer">{{ row.platforms }}</a>
                  <span v-else>{{ row.platforms }}</span>
                </template>
                <template v-slot:[`links-data`]="{ row }">
                  <a :href="row.links" target="_blank" rel="noopener noreferrer">{{ row.links }}</a>
                </template>
              </SimpleTable>
            </transition>
          </div>
        </transition>

      </div>
    </div>
  </div>
</template>


<script>
import {ConsentStorageEnum, CookieConsentNamesEnum} from '../../../../vue/cookies/store/cookiesModule'
import {setCookie} from '../../../js/es6/utilities/cookie-access'
import {mapActions, mapGetters} from 'vuex'
import SimpleTab from '../../../../admin/js/tab/SimpleTab'
import SimpleTable from '../../../../admin/js/table/components/SimpleTable'
import {isObject} from 'lodash-es'

export default {
  name: 'CookieNotice',
  components: {
    SimpleTab,
    SimpleTable,
  },
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showCookieDetails: false,
      rows: [],
      cookieCategories: [],
      selectedCategory: null,
      detailsTableDataLoading: false,
    }
  },
  computed: {
    ...mapGetters([
      'config',
    ]),
    eshopName() {
      return this.t('hct.eshop.name')
    },
    policyUrl() {
      return this.t('hct.eshop.policy.url')
    },
    baseCookiesUrl() {
      const baseUrl = `${window.location.protocol}//${window.location.host}`
      return `${baseUrl}/${this.policyCookies}`
    },
    policyCookies() {
      return this.t('htc.eshop.cookies.url')
    },
    sort() {
      return {
        key: 'categories',
        order: 'desc',
      }
    },
    columns() {
      return [
        {
          key: 'id',
          label: this.t('cookies.table.id'),
          sortable: true,
        },
        {
          key: 'platforms',
          label: this.t('cookies.table.platforms'),
          sortable: true,
        },
        {
          key: 'categories',
          label: this.t('cookies.table.categories'),
          sortable: true,
        },
        {
          key: 'data_controllers',
          label: this.t('cookies.table.data_controllers'),
          sortable: true,
        },
        {
          key: 'data_key_names',
          label: this.t('cookies.table.data_key_names'),
          sortable: true,
        },
        {
          key: 'domains',
          label: this.t('cookies.table.domains'),
          sortable: true,
        },
        {
          key: 'description',
          label: this.t('cookies.table.description'),
        },
        {
          key: 'retention_period',
          label: this.t('cookies.table.retention_period'),
          sortable: true,
        },
        {
          key: 'links',
          label: this.t('cookies.table.links'),
        },
      ]
    },
    filteredRows() {
      if (this.selectedCategory) {
        return this.rows.filter(row => row.categories === this.selectedCategory)
      }
      return this.rows
    },
    cookieDetailsEnabled() {
      return this.config?.cookieDetailsEnabled
    },
  },
  methods: {
    ...mapActions('cookies', [
      'openSettings',
      'setAllConsentValuesToGranted',
      'updateAllCookiesFromState',
      'updateGtagConsentFromState',
    ]),
    postCookieSettingsAll() {
      setCookie(
        CookieConsentNamesEnum.CONSENT_STATUS,
        true,
        365,
      ).then(() => {
        this.$root.$emit('cookies-modal-close')
      })
    },
    postCookieSettingsEssentials() {
      Promise.all([
        setCookie(
          CookieConsentNamesEnum.CONSENT_STATUS,
          true,
          365,
        ),
        setCookie(
          CookieConsentNamesEnum.CONSENT_AD_STORAGE,
          ConsentStorageEnum.DENIED,
          365,
        ),
        setCookie(
          CookieConsentNamesEnum.CONSENT_ANALYTICS_STORAGE,
          ConsentStorageEnum.DENIED,
          365,
        ),
        setCookie(
          CookieConsentNamesEnum.CONSENT_FUNCTIONALITY_STORAGE,
          ConsentStorageEnum.GRANTED,
          365,
        ),
        setCookie(
          CookieConsentNamesEnum.CONSENT_PERSONALIZATION_STORAGE,
          ConsentStorageEnum.DENIED,
          365,
        ),
        setCookie(
          CookieConsentNamesEnum.CONSENT_AD_USER_DATA,
          ConsentStorageEnum.DENIED,
          365,
        ),
        setCookie(
          CookieConsentNamesEnum.CONSENT_AD_PERSONALIZATION,
          ConsentStorageEnum.DENIED,
          365,
        ),
      ]).then(() => {
        this.$root.$emit('cookies-modal-close')
      })
    },
    async postCookieSettingsAllGrantedAction() {
      await Promise.all([
        setCookie(
          CookieConsentNamesEnum.CONSENT_STATUS,
          true,
          365,
        ),
        this.setAllConsentValuesToGranted(),
        this.updateAllCookiesFromState(),
        this.updateGtagConsentFromState(),
      ])
      this.$root.$emit('cookies-modal-close')
    },
    async toggleDetailsTable() {
      this.showCookieDetails = !this.showCookieDetails
      if (this.rows.length === 0) {
        this.detailsTableDataLoading = true
        await this.fetchCookieDetails()
      }
    },
    async fetchCookieDetails() {
      try {
        const response = await fetch('/cookies', {
          method: 'GET',
          headers: {'Content-Type': 'application/json'},
        })
        if (!response.ok) {
          console.error('Failed to fetch cookie details')
        }
        const data = await response.json()
        if (data && isObject(data)) {
          Object.entries(data).forEach(([key, value]) => {
            this.cookieCategories.push(key)
            if (Array.isArray(value)) {
              value.forEach(item => {
                this.rows.push({
                  id: item.id,
                  platforms: item.platforms,
                  categories: item.categories,
                  data_controllers: item.data_controllers,
                  data_key_names: item.data_key_names,
                  domains: item.domains,
                  description: item.description,
                  retention_period: item.retention_period,
                  links: item.links,
                })
              })
            }
          })
        }
      } catch (error) {
        console.error('Failed to fetch cookie details', error)
      } finally {
        this.detailsTableDataLoading = false
      }
    },
    selectCategory(category) {
      if (this.selectedCategory === category) {
        this.selectedCategory = null
      } else {
        this.selectedCategory = category
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.cookie-details-table {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  ::v-deep table {
    width: 100%;
    border-collapse: collapse;

    thead {
      th {
        border-bottom: 1px solid #dddddd;
        border-right: 1px solid #dddddd;
        text-align: left;
        white-space: normal;

        span {
          font-size: 0.875rem;
        }

        button {
          padding: 0 0 1.5px;

          span {
            font-weight: bold;
            font-size: 0.875rem;
          }

          i {
            font-size: 11px;
          }
        }
      }
    }

    tbody {
      tr {
        td {
          border-bottom: 1px solid #dddddd;
          border-right: 1px solid #dddddd;
          text-align: left;
          font-size: 0.875rem;
        }
      }
    }
  }
}

.cookie-details-skeleton {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
}

.skeleton-row {
  background-color: #e0e0e0;
  height: 24px;
  margin-bottom: 10px;
  border-radius: 4px;
  animation: skeleton-animation 1.5s infinite ease-in-out;
}

@keyframes skeleton-animation {
  0% {
    background-color: #e0e0e0;
  }
  50% {
    background-color: #f0f0f0;
  }
  100% {
    background-color: #e0e0e0;
  }
}

.fade-enter-active {
  transition: all .1s ease-in-out;
}

.fade-leave-active {
  transition: none;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.native_slider-lg {
  @media screen and (min-width: 768px) {
    grid-auto-flow: row;
  }
  @media screen and (max-width: 767px) {
    grid-auto-columns: 25%;
    padding-bottom: 0;
    margin-bottom: 0;
    clip-path: inset(0 0 0px 0);
  }
}
</style>


